import React, {useCallback, useEffect, useRef} from "react";
import {InputProps} from "../../interface";
import {manuallyGeocode, handleScriptLoad} from "./google-maps";
import {Container, Input as StyledInput, Label} from "../style";
import {Address as A, IAddressDetails} from "@natomas-org/core";
import {DefaultError} from "../../generic/DefaultError";

const Address = (props: InputProps) => {
  const {
    id,
    label,
    // use the propOnChange alias for the forceChange
    onChange,
    setObject,
    additionalContent,
    setError,
    error,
    value,
    defaultValue,
    ...restProps
  } = props;
  const autoCompleteRef = useRef<HTMLInputElement | null>(null);
  const isInitializedRef = useRef<boolean>(false);

  // Write the value to the input field
  const forceChange = useCallback(
    (value: string) => {
      return onChange?.({
        target: {
          id,
          value: value ?? "",
        },
      } as React.ChangeEvent<HTMLInputElement>);
    },
    [id, onChange]
  );

  // Validate the IAAddressDetails object - memoized with stable dependencies
  const validateAddressObject = useCallback(
    (values: IAddressDetails | null) => {
      if (values && values?.latitude && values?.longitude) {
        const formatted = A.getFullAddress(values);
        setError?.(undefined);
        setObject?.(values);
        
        // Mark that we're about to call forceChange from validation
        // This prevents the subsequent handleChange from triggering manual geocoding
        const input = autoCompleteRef.current;
        if (input) {
          (input as any)._skipNextValidation = true;
        }
        
        forceChange(formatted);
      } else {
        setError?.("Please enter a valid address.");
        setObject?.(undefined);
      }
    },
    [forceChange, setError, setObject]
  );

  // Search for the address, create the IAAddressDetails object, and validate it
  const validateAddress = useCallback(
    async (preformatted: string) => {
      await manuallyGeocode(
        preformatted,
        validateAddressObject
      );
    },
    [validateAddressObject]
  );

  // Handle the input change event, validate the address, and update the input field
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      // Don't prevent default here - let Google autocomplete work
      const {value} = event.target;
      const input = autoCompleteRef.current;
      
      // Check if we should skip validation (e.g., from validateAddressObject calling forceChange)
      const shouldSkipValidation = input && (input as any)._skipNextValidation;
      if (shouldSkipValidation) {
        (input as any)._skipNextValidation = false;
        forceChange(value);
        return;
      }
      
      // Check if this change is coming from Google autocomplete
      const isGoogleSelection = input && (input as any)._hasGoogleSelection;
      
      if (isGoogleSelection) {
        // If this is a Google selection, just update the value and don't trigger manual geocoding
        forceChange(value);
        setError?.(undefined);
        // Clear the flag after using it
        (input as any)._hasGoogleSelection = false;
        return;
      }
      
      // This is a manual typing event
      // Assign typed values immediately
      forceChange(value);
      // Clear any existing error when user starts typing
      setError?.(undefined);
      
      // Only validate if there's a meaningful value (more than just a few characters)
      // And delay validation to allow Google autocomplete to work first
      if (value.length > 5) {
        // Add a small delay to let Google autocomplete work first
        setTimeout(() => {
          // Double-check that Google autocomplete didn't fire in the meantime
          const stillNeedsManualGeocode = !(input && (input as any)._hasGoogleSelection);
          if (stillNeedsManualGeocode) {
            return validateAddress(value);
          }
        }, 500);
      }
    },
    [forceChange, setError, validateAddress]
  );

  // Handle keydown to prevent form submission on Enter when Google suggestions are showing
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        // Check if Google autocomplete dropdown is open
        const input = autoCompleteRef.current;
        if (input && (input as any)._autocomplete) {
          // Prevent form submission to allow Google autocomplete selection
          event.preventDefault();
          event.stopPropagation();
        }
      }
    },
    []
  );

  // Handle the paste event, validate the address, and update the input field
  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      // Prevent the default paste behavior if needed
      event.preventDefault();
      // Extract the pasted text from the clipboard event
      const preformatted = event.clipboardData.getData("text");
      return validateAddress(preformatted);
    },
    [validateAddress]
  );

  // Load the Google Maps API script - only once per component mount
  useEffect(() => {
    // Only initialize once per component mount
    if (isInitializedRef.current) {
      return;
    }
    
    // Copy ref value to avoid stale closure issues
    const inputElement = autoCompleteRef.current;
    
    // Only initialize if the ref is properly attached to a DOM element
    if (inputElement && inputElement instanceof HTMLInputElement) {
      // Check if Google Maps is loaded and Autocomplete is available
      const googleMaps = (window as any).google;
      
      if (googleMaps && googleMaps.maps && googleMaps.maps.places) {
        handleScriptLoad(autoCompleteRef, validateAddressObject);
        isInitializedRef.current = true;
      }
    }

    // Cleanup function
    return () => {
      if (inputElement && (inputElement as any)._autocomplete) {
        // Clean up the Google Maps Autocomplete instance
        const googleMaps = (window as any).google;
        googleMaps?.maps?.event?.clearInstanceListeners?.((inputElement as any)._autocomplete);
        (inputElement as any)._autocomplete = null;
      }
      isInitializedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount/unmount

  return (
    <Container key={`${id}-container`}>
      <Label htmlFor={id} id={`${id}-label`} hidden={!label}>
        {label}
      </Label>
      <StyledInput
        {...restProps}
        key={`${id}-input`}
        type={"search"}
        id={id}
        name={id}
        defaultValue={defaultValue}
        onPaste={handlePaste}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        autoComplete={"shipping street-address"}
        ref={autoCompleteRef}
        data-private
      />
      {additionalContent}
      <DefaultError id={id} error={error} />
    </Container>
  );
};

export default Address;
